import React, { useState, useEffect, useMemo } from 'react';
import './usertools-css/VitaminBiotinCalculator.css';
import '../../styles/components/ainutritionlandingpage.css';

// --- Constants ---
const biotinAiRdaTable = { // µg/day (micrograms)
    '0-6m': { value: 5, isAI: true },
    '7-12m': { value: 6, isAI: true },
    '1-3y': { value: 8, isAI: true },
    '4-8y': { value: 12, isAI: true },
    '9-13y': { value: 20, isAI: true },
    '14-18y': { value: 25, isAI: true },
    '19+y': { value: 30, isAI: true },
    'pregnant': { value: 30, isAI: true },
    'lactating': { value: 35, isAI: true },
};

// NEW: Biotin Food Sources Data (Estimates)
const BIOTIN_FOOD_SOURCES = [
    /* very high (≥30 µg/serving) */
    { id: 'liverBeef', name: 'Beef Liver, cooked', serving: '3 oz (85 g)', mcg: 31 },
    { id: 'liverChicken', name: 'Chicken Liver, cooked', serving: '3 oz (85 g)', mcg: 138 }, // organ meats top the chart

    /* high (10–30 µg) */
    { id: 'egg', name: 'Egg, large, cooked', serving: '1 whole', mcg: 10 },
    { id: 'eggYolkRaw', name: 'Egg Yolk, raw*', serving: '1 yolk', mcg: 25 },
    { id: 'yeast', name: 'Baker’s Yeast, dry', serving: '¼ oz (7 g)', mcg: 14 },

    /* moderate (3–10 µg) */
    { id: 'hamburger', name: 'Hamburger Patty, cooked', serving: '3 oz', mcg: 4 },
    { id: 'oysters', name: 'Pacific Oysters, cooked', serving: '6 medium', mcg: 7.5 },
    { id: 'peanuts', name: 'Peanuts, dry-roasted', serving: '¼ cup', mcg: 5 },
    { id: 'porkChop', name: 'Pork Chop, cooked', serving: '3 oz', mcg: 4 },
    { id: 'salmon', name: 'Salmon, pink, canned', serving: '3 oz', mcg: 5 },
    { id: 'shiitake', name: 'Shiitake Mushrooms, cooked', serving: '½ cup', mcg: 2.3 },
    { id: 'soybeans', name: 'Soybeans, boiled', serving: '½ cup', mcg: 7 },
    { id: 'walnuts', name: 'Walnuts, chopped', serving: '¼ cup', mcg: 4.5 },

    /* modest (1–3 µg) */
    { id: 'almonds', name: 'Almonds, roasted', serving: '¼ cup', mcg: 1.5 },
    { id: 'avocado', name: 'Avocado', serving: '½ fruit', mcg: 1.8 },
    { id: 'chiaSeeds', name: 'Chia Seeds', serving: '1 Tbsp', mcg: 1.2 },
    { id: 'lentils', name: 'Lentils, boiled', serving: '½ cup', mcg: 1.3 },
    { id: 'sunflowerSeeds', name: 'Sunflower Seeds, roasted', serving: '¼ cup', mcg: 2.6 },
    { id: 'sweetPotato', name: 'Sweet Potato, baked', serving: '½ cup', mcg: 2.4 },

    /* low (<1 µg) — still useful for mixed diets */
    { id: 'banana', name: 'Banana, sliced', serving: '½ cup', mcg: 0.1 },
    { id: 'broccoli', name: 'Broccoli, raw', serving: '½ cup', mcg: 0.4 },
    { id: 'cheddar', name: 'Cheddar Cheese, mild', serving: '1 oz', mcg: 0.4 },
    { id: 'milk', name: 'Milk, 2 %', serving: '1 cup', mcg: 0.3 },
    { id: 'oatmeal', name: 'Oatmeal, cooked', serving: '1 cup', mcg: 0.2 },
    { id: 'spinach', name: 'Spinach, boiled', serving: '½ cup', mcg: 0.5 },
    { id: 'tunaCanned', name: 'Tuna, canned in water', serving: '3 oz', mcg: 0.6 },
    { id: 'yogurt', name: 'Yogurt, plain', serving: '1 cup', mcg: 0.2 }
];


const MILD_DEF_THRESHOLD_FACTOR = 0.40;
const SEVERE_DEF_THRESHOLD_FACTOR = 0.25;
const HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG = 1000;
const ASSAY_RISK_SUPPLEMENT_THRESHOLD_MCG = 2000;
const ASSAY_RISK_TOTAL_THRESHOLD_MCG = 2050;
const STOP_TIME_LOW_DOSE_HOURS = 48;
const STOP_TIME_HIGH_DOSE_HOURS = 72;

const biotinRiskMedications = {
    antiepileptics: "Antiepileptic Drugs (e.g., Carbamazepine, Phenytoin, Valproate)",
    isotretinoin: "Isotretinoin (Accutane)",
    long_term_antibiotics: "Long-term Broad-Spectrum Antibiotics",
};

// --- Helper Functions ---
const parseMicrograms = (value) => {
    const num = parseFloat(value);
    return isNaN(num) || num < 0 ? 0 : num;
};

function daysUntil(dateStr) {
    if (!dateStr) return null;
    try {
        const parts = dateStr.split('-');
        if (parts.length !== 3) return null;
        const targetDate = new Date(parts[0], parts[1] - 1, parts[2]);
        const today = new Date();
        targetDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        if (isNaN(targetDate.getTime())) return null;
        const diffTime = targetDate.getTime() - today.getTime();
        if (diffTime < 0) return -1;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    } catch (e) {
        console.error("Error parsing date:", e);
        return null;
    }
}

// --- Main Calculation Logic ---
// UPDATED: Accepts dietaryIntakeMcg as a number directly
const calculateBiotinLogic = (inputs) => {
    const { age, sex, isPregnant, isLactating,
        dietaryIntakeMcg, // *** CHANGED: Now expects a number ***
        supplements,
        upcomingLabTestDate,
        medicationsBiotin
    } = inputs;

    // ... (rest of the calculation logic remains the same) ...
    // ... EXCEPT where dietaryIntakeMcg was referenced, it's now directly the number ...

    let results = {
        targetAiRda: null, dietaryIntakeMcg: 0, supplementIntakeMcg: 0, totalIntakeMcg: 0,
        percentOfAiRda: 0, safetyFlags: [], recommendations: [], explanation: '',
        stopBiotinInfo: null, error: null
    };
    let explanation = [];
    let safetyFlags = [];
    let recommendations = [];

    const ageNum = parseInt(age, 10); // Age is already validated before calling
    if (isNaN(ageNum) || ageNum < 0) return { ...results, error: "Invalid Age." };
    if (!sex) return { ...results, error: "Sex not selected." };

    // Determine Target AI/RDA (logic unchanged)
    let targetAiRda = 0;
    let lifeStageKey = '';
    if (isLactating && sex === 'female') lifeStageKey = 'lactating';
    else if (isPregnant && sex === 'female') lifeStageKey = 'pregnant';
    else {
        if (ageNum < 0.58) lifeStageKey = '0-6m';
        else if (ageNum < 1) lifeStageKey = '7-12m';
        else if (ageNum <= 3) lifeStageKey = '1-3y';
        else if (ageNum <= 8) lifeStageKey = '4-8y';
        else if (ageNum <= 13) lifeStageKey = '9-13y';
        else if (ageNum <= 18) lifeStageKey = '14-18y';
        else lifeStageKey = '19+y';
    }
    if (biotinAiRdaTable[lifeStageKey]) {
        targetAiRda = biotinAiRdaTable[lifeStageKey].value;
        explanation.push(`1. Target Adequate Intake (AI) for your life stage (${lifeStageKey}): ${targetAiRda} µg/day.`);
    } else {
        targetAiRda = 30;
        explanation.push(`1. Using default adult AI: ${targetAiRda} µg/day.`);
    }

    // 2. Record Dietary Intake (already calculated number passed in)
    // ** REMOVED: const dietaryIntakeMcg = parseMicrograms(dietaryBiotinIntakeMcgStr); **
    explanation.push(`2. Estimated Dietary Biotin Intake (from selections): ${dietaryIntakeMcg} µg/day.`); // Use the passed number
    recommendations.push("Ensure food selections reflect typical daily intake. Biotin-rich foods include eggs (cooked), salmon, pork, beef liver, avocado, nuts, seeds, and sweet potatoes.");
    recommendations.push("Avoid regular consumption of raw egg whites, as avidin binds biotin and prevents absorption.");


    // 3. Calculate Supplement Intake (logic unchanged)
    let totalSupplementIntakeMcg = 0;
    let maxSingleSupplementDose = 0;
    supplements.forEach((sup, index) => {
        const dose = parseMicrograms(sup.doseMcg);
        const servings = parseFloat(sup.servingsPerDay) || 0;
        if (dose > 0 && servings > 0) {
            const dailyIntake = dose * servings;
            totalSupplementIntakeMcg += dailyIntake;
            explanation.push(`   - Supplement ${index + 1}: ${dose} µg/serving * ${servings} servings/day = ${dailyIntake.toFixed(0)} µg/day.`);
            if (dose > maxSingleSupplementDose) maxSingleSupplementDose = dose;
        }
    });
    explanation.push(`3. Total Biotin from Supplements: ${totalSupplementIntakeMcg.toFixed(0)} µg/day.`);

    // 4. Calculate Total Intake & %AI/RDA (logic unchanged, uses dietaryIntakeMcg number)
    const totalBiotinIntakeMcg = dietaryIntakeMcg + totalSupplementIntakeMcg;
    explanation.push(`4. Estimated Total Daily Biotin Intake: ${totalBiotinIntakeMcg.toFixed(0)} µg/day.`);
    const percentOfAiRda = targetAiRda > 0 ? (totalBiotinIntakeMcg / targetAiRda) * 100 : 0;
    explanation.push(`   = ${percentOfAiRda.toFixed(0)}% of Target AI (${targetAiRda} µg/day).`);

    // 5. Deficiency & Risk Flags (logic unchanged)
    const mildDefThreshold = targetAiRda * MILD_DEF_THRESHOLD_FACTOR;
    const severeDefThreshold = targetAiRda * SEVERE_DEF_THRESHOLD_FACTOR;
    if (totalBiotinIntakeMcg < severeDefThreshold) safetyFlags.push({ type: 'severe', text: `Intake (<${severeDefThreshold.toFixed(1)} µg) is <25% of AI. Risk of SEVERE deficiency. Seek medical advice.` });
    else if (totalBiotinIntakeMcg < mildDefThreshold) safetyFlags.push({ type: 'warning', text: `Intake (<${mildDefThreshold.toFixed(1)} µg) is <40% of AI. Risk of MILD deficiency.` });

    if (totalSupplementIntakeMcg >= HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG) safetyFlags.push({ type: 'info', text: `Supplement intake (≥${HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG} µg) is high. Doses >100 µg may not provide extra benefit.` });

    const assayRiskFromSupps = totalSupplementIntakeMcg >= ASSAY_RISK_SUPPLEMENT_THRESHOLD_MCG;
    const assayRiskFromTotal = totalBiotinIntakeMcg >= ASSAY_RISK_TOTAL_THRESHOLD_MCG;
    let assayRiskFlag = false;
    if (assayRiskFromSupps || assayRiskFromTotal) {
        assayRiskFlag = true;
        safetyFlags.push({ type: 'severe', text: `High Biotin Intake (≥${assayRiskFromSupps ? ASSAY_RISK_SUPPLEMENT_THRESHOLD_MCG : ASSAY_RISK_TOTAL_THRESHOLD_MCG} µg) significantly interferes with lab tests.` });
    }

    if ((isPregnant || isLactating) && sex === 'female' && totalBiotinIntakeMcg < targetAiRda) safetyFlags.push({ type: 'warning', text: `Intake during ${isPregnant ? 'pregnancy' : 'lactation'} is below recommended ${targetAiRda} µg/day.` });

    let hasRiskMeds = false;
    Object.keys(medicationsBiotin).forEach(key => {
        if (medicationsBiotin[key]) {
            hasRiskMeds = true;
            safetyFlags.push({ type: 'warning', text: `Use of ${biotinRiskMedications[key] || key} may affect biotin needs. Discuss with doctor.` });
        }
    });
    if (hasRiskMeds) recommendations.push("Inform doctor about biotin intake and medications.");

    // Lab Test Stop Timer Logic (logic unchanged)
    let stopBiotinInfo = null;
    if (assayRiskFlag && upcomingLabTestDate) {
        const daysRemaining = daysUntil(upcomingLabTestDate);
        if (daysRemaining !== null && daysRemaining >= 0) {
            let hoursToStop = 0;
            if (maxSingleSupplementDose >= 5000 || totalSupplementIntakeMcg >= 5000) hoursToStop = STOP_TIME_HIGH_DOSE_HOURS;
            else if (maxSingleSupplementDose >= HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG || totalSupplementIntakeMcg >= HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG) hoursToStop = STOP_TIME_LOW_DOSE_HOURS;

            if (hoursToStop > 0) {
                const daysToStop = Math.ceil(hoursToStop / 24);
                const targetDate = new Date(upcomingLabTestDate.split('-')[0], upcomingLabTestDate.split('-')[1] - 1, upcomingLabTestDate.split('-')[2]);
                const stopDate = new Date(targetDate);
                stopDate.setDate(targetDate.getDate() - daysToStop);
                const stopDateString = stopDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
                const testDateString = targetDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

                if (daysRemaining < daysToStop) {
                    stopBiotinInfo = `🛑 Lab test is too soon (${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'})! Stop biotin supplements IMMEDIATELY. Needed to stop ~${daysToStop} days before (${stopDateString}). Inform lab/doctor.`;
                    safetyFlags.push({ type: 'severe', text: `Lab Test Alert: Stop biotin IMMEDIATELY for test on ${testDateString}. Insufficient time.` });
                } else {
                    stopBiotinInfo = `✋ For lab test on ${testDateString}, stop biotin supplements ≥${hoursToStop} hours (${daysToStop} days) before. Stop by end of day: ${stopDateString}.`;
                    recommendations.push(stopBiotinInfo);
                }
            } else {
                stopBiotinInfo = "ℹ️ High total biotin detected. Lab interference possible. Inform doctor/lab.";
                recommendations.push(stopBiotinInfo);
            }
        } else if (daysRemaining === -1) stopBiotinInfo = "ℹ️ Lab test date is in the past.";
        else stopBiotinInfo = "ℹ️ Could not parse lab test date (use YYYY-MM-DD).";
        if (stopBiotinInfo && daysRemaining !== -1) recommendations.push(stopBiotinInfo); // Add non-past info to recs

    } else if (assayRiskFlag && !upcomingLabTestDate) {
        const defaultStopTime = (maxSingleSupplementDose >= 5000 || totalSupplementIntakeMcg >= 5000) ? STOP_TIME_HIGH_DOSE_HOURS : STOP_TIME_LOW_DOSE_HOURS;
        const defaultStopDays = Math.ceil(defaultStopTime / 24);
        recommendations.push(`If lab tests scheduled, stop high-dose biotin ${defaultStopTime} hours (${defaultStopDays} days) before. Enter test date for specific guidance.`);
    }

    // Final General Recommendations (logic unchanged)
    if (totalBiotinIntakeMcg >= mildDefThreshold && totalBiotinIntakeMcg <= targetAiRda * 1.5) recommendations.push("Estimated intake appears adequate or near-adequate.");
    else if (totalBiotinIntakeMcg > targetAiRda * 1.5 && totalSupplementIntakeMcg < HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG) recommendations.push("Intake above AI, primarily from diet/standard MVI, generally safe.");

    recommendations.push("Consult a healthcare professional for personalized advice, especially with symptoms, medications, pregnancy/lactation, or lab tests.");

    // Structure Final Results (logic unchanged)
    results = {
        ...results, targetAiRda, dietaryIntakeMcg,
        supplementIntakeMcg: parseFloat(totalSupplementIntakeMcg.toFixed(0)),
        totalIntakeMcg: parseFloat(totalBiotinIntakeMcg.toFixed(0)),
        percentOfAiRda: parseFloat(percentOfAiRda.toFixed(0)),
        safetyFlags, recommendations, explanation: explanation.join('\n'), stopBiotinInfo
    };
    return results;
};


// --- Component: VitaminBiotinCalculator ---
function VitaminBiotinCalculator() {
    // --- State ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState('');
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);
    // REMOVED: const [dietaryBiotinIntakeMcgStr, setDietaryBiotinIntakeMcgStr] = useState('');
    const [supplements, setSupplements] = useState([{ id: 1, doseMcg: '', servingsPerDay: '' }]);
    const [upcomingLabTestDate, setUpcomingLabTestDate] = useState('');
    const [medicationsBiotin, setMedicationsBiotin] = useState(
        Object.keys(biotinRiskMedications).reduce((acc, key) => ({ ...acc, [key]: false }), {})
    );

    // NEW State for Food Selection
    const [selectedFoods, setSelectedFoods] = useState({}); // Stores { foodId: boolean }

    const [calculationResult, setCalculationResult] = useState(null); // Stores the final results object
    const [error, setError] = useState(''); // Stores validation or calculation errors
    const [consentGiven] = useState(true); // Placeholder

    // --- Derived State: Calculate dietary biotin from selected foods ---
    const calculatedDietaryBiotin = useMemo(() => {
        let total = 0;
        for (const food of BIOTIN_FOOD_SOURCES) {
            if (selectedFoods[food.id]) {
                total += food.mcg;
            }
        }
        return total;
    }, [selectedFoods]); // Recalculate only when selectedFoods changes

    // --- Input Handlers ---
    const handleSupplementChange = (id, field, value) => {
        setSupplements(prev => prev.map(sup => sup.id === id ? { ...sup, [field]: value } : sup));
    };

    const addSupplement = () => {
        setSupplements(prev => [...prev, { id: Date.now(), doseMcg: '', servingsPerDay: '' }]);
    };

    const removeSupplement = (id) => {
        if (supplements.length > 1) setSupplements(prev => prev.filter(sup => sup.id !== id));
        else setSupplements([{ id: 1, doseMcg: '', servingsPerDay: '' }]); // Clear last one
    };

    const handleMedicationChange = (event) => {
        const { name, checked } = event.target;
        setMedicationsBiotin(prev => ({ ...prev, [name]: checked }));
    };

    // NEW: Handler for food checkbox changes
    const handleFoodSelectionChange = (event) => {
        const { name, checked } = event.target; // name will be the food.id
        setSelectedFoods(prev => ({
            ...prev,
            [name]: checked
        }));
        // Clear results when input changes, require recalculation
        setCalculationResult(null);
        setError('');
    };

    // NEW: Handler to clear all food selections
    const clearFoodSelections = () => {
        setSelectedFoods({});
        // Clear results when input changes, require recalculation
        setCalculationResult(null);
        setError('');
    };

    // Effect to manage Pregnancy/Lactation state based on Sex/Age (Unchanged)
    useEffect(() => {
        const ageNum = parseInt(age, 10);
        const isFemale = sex === 'female';
        const isChildBearingAge = !isNaN(ageNum) && ageNum >= 12 && ageNum <= 55;
        if (!isFemale || !isChildBearingAge) {
            setIsPregnant(false);
            setIsLactating(false);
        }
        if (isLactating) setIsPregnant(false);

        // Clear results when profile changes, require recalculation
        if (calculationResult) {
            setCalculationResult(null);
            setError('');
        }
    }, [sex, age, isLactating, calculationResult]);

    // Effect to clear results if supplement/meds/date changes
    useEffect(() => {
        setCalculationResult(null);
        setError('');
    }, [supplements, medicationsBiotin, upcomingLabTestDate]);

    // --- REMOVED: Real-time Calculation useEffect ---

    // --- NEW: Handler for the "Calculate" Button ---
    const handleCalculateClick = () => {
        // --- Input Validation (Performed on Click) ---
        const ageNum = parseInt(age, 10);
        const isAgeValid = !isNaN(ageNum) && ageNum >= 0 && ageNum <= 120;
        const isSexValid = !!sex;

        // Check if any intake is provided (dietary selection OR supplements)
        const hasDietaryInput = calculatedDietaryBiotin > 0;
        const hasSupplementInput = supplements.some(sup => parseMicrograms(sup.doseMcg) > 0 && parseFloat(sup.servingsPerDay) > 0);
        const hasAnyIntakeData = hasDietaryInput || hasSupplementInput;

        // Gate 1: Basic required fields
        if (!isAgeValid || !isSexValid) {
            setError('Please enter a valid Age (0-120) and select Sex.');
            setCalculationResult(null);
            return;
        }

        // Gate 2: At least one intake source needed
        if (!hasAnyIntakeData) {
            setError('Please select Dietary Foods OR add Supplement details.');
            setCalculationResult(null);
            return;
        }

        // If validation passes:
        setError(''); // Clear any previous errors

        const inputs = {
            age: ageNum,
            sex,
            isPregnant,
            isLactating,
            dietaryIntakeMcg: calculatedDietaryBiotin, // Pass the calculated number
            supplements,
            upcomingLabTestDate,
            medicationsBiotin
        };

        // Call the main logic function
        const result = calculateBiotinLogic(inputs);

        // Update state with the results or errors from the logic function
        if (result.error) {
            setError(result.error);
            setCalculationResult(null);
        } else {
            setCalculationResult(result); // Display results
            setError('');
        }
    };


    // --- JSX Structure ---
    return (
        <div className={`biotinCalculator ${!consentGiven ? 'blurred' : ''}`}>
            <h1>
                <span>
                    <span role="img" aria-label="biotin calculator">
                        💊
                    </span>{' '}
                    Biotin (Vitamin B7) Calculator
                </span>

                <span className="ai-promo-inline">
                    <strong>Unlock Smarter Eating:</strong>{' '}
                    Get a complete breakdown of vitamins, minerals, and fats plus a personalized
                    weight-loss plan or nutrition tailored to your training with a single{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <div style={{ textAlign: 'center' }}>
                <h2>Hair, Skin, Nails</h2>
            </div>
            <p className="fact-blurb">
                <strong>What are the benefits of biotin?</strong> Biotin functions as a coenzyme in carboxylase reactions that support fatty acid synthesis and protein (keratin) production. Meeting the Adequate Intake (30&nbsp;µg/day for adults) helps maintain healthy hair, skin and nails while mega-doses above&nbsp;100 µg rarely offer additional benefits unless a deficiency is present.
            </p>
            <p className="important-note disclaimer">
                <strong>Disclaimer </strong>
                This tool provides <em>approximate</em> biotin targets based on your inputs and public data. It is <i>not</i> medical advice and does not diagnose or treat conditions. Always verify supplement use, diet changes, pregnancy/lactation needs, or drug interactions with a healthcare professional. For doses ≥ 1 000 µg/day, notify your clinician and stop 48–72 h before blood tests—high biotin can skew lab results.
            </p>



            <fieldset disabled={!consentGiven} className="calculator-form">
                <legend className="sr-only">Biotin Calculator Inputs</legend>

                {/* Basic Info Section */}
                <section className="form-section" aria-labelledby="profile-heading">
                    <h2 id="profile-heading">👤 Your Profile</h2>
                    <div className="input-group">
                        <label htmlFor="age">Age (Years):</label>
                        <input
                            id="age"
                            type="number"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            required
                            min="0"
                            max="120"
                            placeholder="e.g., 35"
                        />
                        <label htmlFor="sex">Sex Assigned at Birth:</label>
                        <select
                            id="sex"
                            value={sex}
                            onChange={(e) => setSex(e.target.value)}
                            required
                        >
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                        {sex === 'female' && parseInt(age, 10) >= 12 && parseInt(age, 10) <= 55 && (
                            <div className="pregnancy-lactation-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isPregnant}
                                        onChange={(e) => setIsPregnant(e.target.checked)}
                                        disabled={isLactating}
                                    />
                                    Currently Pregnant
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={isLactating}
                                        onChange={(e) => setIsLactating(e.target.checked)}
                                    />
                                    Currently Lactating
                                </label>
                            </div>
                        )}
                    </div>
                </section>

                {/* Dietary Intake Section */}
                <section className="form-section" aria-labelledby="diet-heading">
                    <h2 id="diet-heading">🍳 Dietary Biotin Estimate (Select Foods Eaten Daily)</h2>
                    <p className="instructions">
                        Check the boxes for foods you consume on a typical day to estimate your dietary biotin intake. Values are approximate.
                    </p>
                    <div className="food-selection-grid">
                        {/* Very High (≥30 µg/serving) */}
                        <h3>Very High (≥30 µg/serving)</h3>
                        {BIOTIN_FOOD_SOURCES.filter(food => food.mcg >= 30).map(food => (
                            <label key={food.id} className="food-item-label">
                                <input
                                    type="checkbox"
                                    name={food.id}
                                    checked={!!selectedFoods[food.id]}
                                    onChange={handleFoodSelectionChange}
                                />
                                {food.name} ({food.serving})
                                <span className="food-mcg">~{food.mcg} µg</span>
                            </label>
                        ))}

                        {/* High (10–30 µg/serving) */}
                        <h3>High (10–30 µg/serving)</h3>
                        {BIOTIN_FOOD_SOURCES.filter(food => food.mcg >= 10 && food.mcg < 30).map(food => (
                            <label key={food.id} className="food-item-label">
                                <input
                                    type="checkbox"
                                    name={food.id}
                                    checked={!!selectedFoods[food.id]}
                                    onChange={handleFoodSelectionChange}
                                />
                                {food.name} ({food.serving})
                                <span className="food-mcg">~{food.mcg} µg</span>
                            </label>
                        ))}

                        {/* Moderate (3–10 µg/serving) */}
                        <h3>Moderate (3–10 µg/serving)</h3>
                        {BIOTIN_FOOD_SOURCES.filter(food => food.mcg >= 3 && food.mcg < 10).map(food => (
                            <label key={food.id} className="food-item-label">
                                <input
                                    type="checkbox"
                                    name={food.id}
                                    checked={!!selectedFoods[food.id]}
                                    onChange={handleFoodSelectionChange}
                                />
                                {food.name} ({food.serving})
                                <span className="food-mcg">~{food.mcg} µg</span>
                            </label>
                        ))}

                        {/* Modest (1–3 µg/serving) */}
                        <h3>Modest (1–3 µg/serving)</h3>
                        {BIOTIN_FOOD_SOURCES.filter(food => food.mcg >= 1 && food.mcg < 3).map(food => (
                            <label key={food.id} className="food-item-label">
                                <input
                                    type="checkbox"
                                    name={food.id}
                                    checked={!!selectedFoods[food.id]}
                                    onChange={handleFoodSelectionChange}
                                />
                                {food.name} ({food.serving})
                                <span className="food-mcg">~{food.mcg} µg</span>
                            </label>
                        ))}

                        {/* Low (<1 µg/serving) */}
                        <h3>Low (&lt;1 µg/serving)</h3>
                        {BIOTIN_FOOD_SOURCES.filter(food => food.mcg < 1).map(food => (
                            <label key={food.id} className="food-item-label">
                                <input
                                    type="checkbox"
                                    name={food.id}
                                    checked={!!selectedFoods[food.id]}
                                    onChange={handleFoodSelectionChange}
                                />
                                {food.name} ({food.serving})
                                <span className="food-mcg">~{food.mcg} µg</span>
                            </label>
                        ))}
                    </div>
                    <div className="dietary-total-display">
                        <button
                            type="button"
                            onClick={clearFoodSelections}
                            className="clear-button"
                        >
                            Clear Food Selections
                        </button>
                        <strong>
                            Estimated Dietary Biotin Total: <span>{calculatedDietaryBiotin.toFixed(1)} µg / day</span>
                        </strong>
                    </div>
                </section>

                {/* Supplement Intake Section */}
                <section className="form-section" aria-labelledby="supplements-heading">
                    <h2 id="supplements-heading">💊 Biotin Supplement Intake</h2>
                    <p className="supplement-info">
                        Please enter details for any biotin-related supplements you are taking, such as multivitamins, hair/nail supplements, or standalone biotin supplements.
                    </p>
                    {supplements.map((sup, index) => (
                        <div key={sup.id} className="supplement-entry input-group">
                            <label htmlFor={`sup_dose_${sup.id}`}>
                                Supplement {index + 1} Dose (µg per serving):
                            </label>
                            <input
                                id={`sup_dose_${sup.id}`}
                                type="number"
                                value={sup.doseMcg}
                                onChange={(e) => handleSupplementChange(sup.id, 'doseMcg', e.target.value)}
                                min="0"
                                placeholder="e.g., 5000"
                                aria-label={`Supplement ${index + 1} Dose in Micrograms`}
                            />
                            <label htmlFor={`sup_servings_${sup.id}`}>
                                Servings per Day:
                            </label>
                            <input
                                id={`sup_servings_${sup.id}`}
                                type="number"
                                value={sup.servingsPerDay}
                                onChange={(e) => handleSupplementChange(sup.id, 'servingsPerDay', e.target.value)}
                                min="0"
                                step="0.1"
                                placeholder="e.g., 1 or 0.5"
                                aria-label={`Supplement ${index + 1} Servings per Day`}
                            />
                            {supplements.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeSupplement(sup.id)}
                                    className="remove-button"
                                    aria-label={`Remove Supplement ${index + 1}`}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={addSupplement} className="clear-button">
                        + Add Another Supplement
                    </button>
                </section>

                {/* Lab Tests & Medications Section */}
                <section className="form-section" aria-labelledby="labs-meds-heading">
                    <h2 id="labs-meds-heading">🧪 Lab Tests & Medications</h2>
                    <div className="input-group">
                        <label htmlFor="labTestDate">Upcoming Lab Test Date (Optional):</label>
                        <input
                            id="labTestDate"
                            type="date"
                            value={upcomingLabTestDate}
                            onChange={(e) => setUpcomingLabTestDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                        />
                        <small>
                            Enter date if taking ≥{HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG} µg/day for stop-timing advice.
                        </small>
                    </div>
                    <fieldset className="medication-fieldset">
                        <legend>Check Medications Known to Affect Biotin (Consult Doctor):</legend>
                        {Object.entries(biotinRiskMedications).map(([key, label]) => (
                            <label key={key} className="checkbox-label indented">
                                <input
                                    type="checkbox"
                                    name={key}
                                    checked={medicationsBiotin[key]}
                                    onChange={handleMedicationChange}
                                />
                                {label}
                            </label>
                        ))}
                    </fieldset>
                </section>

                {/* Calculate Button */}
                <div className="calculate-button-container">
                    <button
                        type="button"
                        onClick={handleCalculateClick}
                        className="calculate-button"
                    >
                        Calculate Biotin Status
                    </button>
                </div>
            </fieldset>
            {/* End of main calculator fieldset */}


            {/* --- Error Display Area --- */}
            {error && consentGiven && (
                <div className="error-section main-error" role="alert">
                    <p><strong>Notice:</strong> {error}</p>
                </div>
            )}

            {/* --- Results Display Area (Only shows AFTER calculation) --- */}
            {calculationResult && !error && consentGiven && (
                <div className="results-section" aria-live="polite">
                    <h2>📊 Your Estimated Biotin Status</h2>

                    {/* Safety Alerts */}
                    {calculationResult.safetyFlags?.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Alerts & Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, i) => (
                                    <li key={i} className={`alert-${flag.type}`}>
                                        {flag.type === 'severe' && '🛑 '}
                                        {flag.type === 'warning' && '⚠️ '}
                                        {flag.type === 'info' && 'ℹ️ '}
                                        <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Key Metrics with Icons */}
                    <div className="results-grid biotin-results-grid">
                        {/* Target AI is always “OK” */}
                        <div className="result-item">
                            <span className="status-icon ok">✅</span>
                            <span className="result-label">Target AI:</span>
                            <span className="result-value significant">
                                {calculationResult.targetAiRda} µg/day
                            </span>
                        </div>

                        {/* Dietary Intake vs Target */}
                        <div className="result-item">
                            {calculationResult.dietaryIntakeMcg <= calculationResult.targetAiRda * 10 // Allow up to 10x AI for dietary intake
                                ? <span className="status-icon ok">✅</span>
                                : <span className="status-icon warning">⚠️</span>
                            }
                            <span className="result-label">Estimated Intake from Food:</span>
                            <span className="result-value">
                                {calculationResult.dietaryIntakeMcg} µg/day
                            </span>
                        </div>

                        {/* Supplement Intake */}
                        <div className="result-item">
                            {calculationResult.supplementIntakeMcg <= HIGH_DOSE_SUPPLEMENT_THRESHOLD_MCG
                                ? <span className="status-icon ok">✅</span>
                                : <span className="status-icon warning">⚠️</span>
                            }
                            <span className="result-label">Supplement Intake:</span>
                            <span className="result-value">
                                {calculationResult.supplementIntakeMcg} µg/day
                            </span>
                        </div>

                        {/* Total Intake vs AI */}
                        <div className="result-item">
                            {calculationResult.percentOfAiRda < 100
                                ? <span className="status-icon warning" title={`Intake is below the goal of ${calculationResult.targetAiRda} µg/day`}>⚠️</span> // Show warning if below 100% of AI
                                : calculationResult.percentOfAiRda < (MILD_DEF_THRESHOLD_FACTOR * 100)
                                    ? <span className="status-icon warning" title={`Intake is below the mild deficiency threshold (${(calculationResult.targetAiRda * MILD_DEF_THRESHOLD_FACTOR).toFixed(1)} µg/day)`}>⚠️</span>
                                    : <span className="status-icon ok" title={`Intake meets or exceeds the goal of ${calculationResult.targetAiRda} µg/day`}>✅</span>
                            }
                            <span className="result-label">Total Intake:</span>
                            <span className="result-value significant">
                                {calculationResult.totalIntakeMcg} µg/day
                            </span>
                            <small>({calculationResult.percentOfAiRda}% of AI)</small>
                            <small className="goal-value">(Goal: {calculationResult.targetAiRda} µg/day)</small> {/* Added Goal Display */}
                        </div>

                        {/* Lab Test Guidance */}
                        {calculationResult.stopBiotinInfo && (
                            <div className="result-item stop-biotin-info">
                                <span className="status-icon warning">✋</span>
                                <span className="result-label">Lab Test Guidance:</span>
                                <span className="result-value">{calculationResult.stopBiotinInfo}</span>
                            </div>
                        )}
                    </div>

                    {/* Recommendations */}
                    {calculationResult.recommendations?.length > 0 && (
                        <div className="recommendations-box">
                            <h4>💡 Recommendations & Notes</h4>
                            <ul>
                                {calculationResult.percentOfAiRda < 100 && (
                                    <li>
                                        Your intake is below the recommended AI. Consider incorporating more biotin-rich foods into your diet, such as eggs (cooked), salmon, pork, beef liver, avocado, nuts, seeds, and sweet potatoes.
                                    </li>
                                )}
                                {calculationResult.percentOfAiRda >= 100 && calculationResult.percentOfAiRda <= 150 && (
                                    <li>
                                        Your intake is within the recommended range. Maintain your current dietary habits to ensure adequate biotin levels.
                                    </li>
                                )}
                                {calculationResult.percentOfAiRda > 150 && calculationResult.percentOfAiRda <= 1000 && (
                                    <li>
                                        Your intake is above the AI but within safe limits. High intake from dietary sources is generally safe. Avoid excessive supplementation unless advised by a healthcare professional.
                                    </li>
                                )}
                                {calculationResult.percentOfAiRda > 1000 && (
                                    <li>
                                        Your intake is significantly above the AI. While biotin has low toxicity, extremely high levels (especially from supplements) may interfere with lab tests. Consult a healthcare professional if you are taking high-dose supplements.
                                    </li>
                                )}
                                {calculationResult.supplementIntakeMcg > 0 && (
                                    <li>
                                        If you are taking supplements, ensure the dosage aligns with your needs. Avoid unnecessary high-dose supplements unless prescribed.
                                    </li>
                                )}
                                {calculationResult.stopBiotinInfo && (
                                    <li>{calculationResult.stopBiotinInfo}</li>
                                )}
                                <li>
                                    Consult a healthcare professional for personalized advice, especially if you experience symptoms, take medications, are pregnant/lactating, or have upcoming lab tests.
                                </li>
                            </ul>
                        </div>
                    )}

                    <p className="important-note results-reminder">
                        <strong>Important Reminder:</strong> These results are estimates based on your provided information and do not replace professional medical advice. Always consult a qualified healthcare provider for personalized guidance.
                    </p>

                </div>
            )}


            {/* --- Educational Content Section --- */}
            {consentGiven && (
                <div id="biotin-education" className="educational-section">
                    <h2>Understanding Biotin (Vitamin B7)</h2>

                    <div className="content-block">
                        <h3>What Does Biotin Do?</h3>
                        <p>
                            Biotin is a B vitamin crucial for energy metabolism, helping convert food into fuel. It's essential for:
                        </p>
                        <ul>
                            <li>Carbohydrate, fat, and protein metabolism (as a coenzyme for carboxylases).</li>
                            <li>Fatty acid synthesis (important for healthy skin barrier function).</li>
                            <li>Gluconeogenesis (making glucose from non-carb sources).</li>
                            <li>Amino acid breakdown.</li>
                            <li>Gene regulation (histone biotinylation).</li>
                        </ul>
                    </div>

                    <div className="content-block">
                        <h3>Biotin Deficiency Signs</h3>
                        <p>
                            True deficiency is rare in healthy individuals eating varied diets, but can occur. Symptoms develop gradually:
                        </p>
                        <ul>
                            <li><strong>Early/Mild (&lt;40% AI):</strong> Thinning hair (alopecia), dry/scaly skin rash (especially around eyes, nose, mouth), brittle nails.</li>
                            <li><strong>Moderate/Severe (&lt;25% AI):</strong> Worsening hair loss, red rash, conjunctivitis, fatigue, muscle pain, tingling/numbness, depression, lethargy.</li>
                            <li><strong>Biochemical signs:</strong> Increased urinary excretion of 3-hydroxyisovaleric acid.</li>
                            <li><strong>Infants (Genetic):</strong> Can present early with seizures, hypotonia, developmental delay, acidosis.</li>
                        </ul>
                    </div>

                    <div className="content-block">
                        <h3>Factors Increasing Risk</h3>
                        <ul>
                            <li><strong>Prolonged Raw Egg White Consumption:</strong> Avidin in raw whites binds biotin tightly. Cooking denatures avidin.</li>
                            <li><strong>Certain Medications:</strong> Long-term use of some antiepileptics (e.g., carbamazepine, phenytoin) and isotretinoin can interfere with biotin. Long-term antibiotics may reduce gut synthesis.</li>
                            <li><strong>Alcoholism:</strong> Can inhibit biotin absorption.</li>
                            <li><strong>Pregnancy/Lactation:</strong> Increased biotin turnover; adequate intake is important.</li>
                            <li><strong>Genetic Disorders:</strong> Biotinidase or holocarboxylase synthetase deficiencies prevent proper biotin use/recycling.</li>
                            <li><strong>Malabsorption Conditions:</strong> Conditions like Crohn’s disease may affect absorption.</li>
                        </ul>
                    </div>

                    <div className="content-block">
                        <h3>High Doses & Lab Test Interference</h3>
                        <ul>
                            <li><strong>No UL Set:</strong> Biotin has low toxicity; excess is excreted in urine.</li>
                            <li><strong>Supplement Claims:</strong> Often marketed for hair, skin, and nails. Benefits only if deficient; doses &gt;100 µg offer no extra advantage for healthy individuals.</li>
                            <li>
                                <strong>🚨 MAJOR LAB INTERFERENCE:</strong> High supplemental biotin (≥{ASSAY_RISK_SUPPLEMENT_THRESHOLD_MCG} µg/day) can distort many lab tests using biotin–streptavidin technology.
                            </li>
                            <li><strong>Affected Tests:</strong> Thyroid hormones, troponin, PTH, vitamin D, hCG, LH, FSH, steroid panels, etc.</li>
                            <li>
                                <strong>Mitigation:</strong> Inform your doctor and stop high-dose supplements <strong>at least {STOP_TIME_LOW_DOSE_HOURS}–{STOP_TIME_HIGH_DOSE_HOURS} hours</strong> before blood draws.
                            </li>
                        </ul>
                    </div>

                    <div className="content-block">
                        <h3>Sources</h3>
                        <p className="sources">
                            • NIH Office of Dietary Supplements. Biotin – Fact Sheet for Health Professionals.
                            Available at: <a href="https://ods.od.nih.gov/factsheets/Biotin-HealthProfessional/" target="_blank" rel="noopener noreferrer">https://ods.od.nih.gov/factsheets/Biotin-HealthProfessional/</a><br />

                            • NIH Office of Dietary Supplements. Biotin – Fact Sheet for Consumers.
                            Available at: <a href="https://ods.od.nih.gov/factsheets/Biotin-Consumer/" target="_blank" rel="noopener noreferrer">https://ods.od.nih.gov/factsheets/Biotin-Consumer/</a><br />

                            • EFSA Panel on Dietetic Products, Nutrition and Allergies (2014). Dietary Reference Values for Biotin. EFSA Journal, 12(2), 3580.
                            Available at: <a href="https://www.efsa.europa.eu/en/efsajournal/pub/3580" target="_blank" rel="noopener noreferrer">https://www.efsa.europa.eu/en/efsajournal/pub/3580</a><br />

                            • Mock D.M. (2017). Biotin: From Nutrition to Therapeutics. Journal of Nutrition, 147(8), 1487–1492.
                            Available at: <a href="https://academic.oup.com/jn/article/147/8/1487/4584739" target="_blank" rel="noopener noreferrer">https://academic.oup.com/jn/article/147/8/1487/4584739</a><br />

                            • Peyro Saint Paul L. et al. (2016). Pharmacokinetics of High-Dose Biotin. Expert Opinion on Drug Metabolism & Toxicology, 12(3), 327–344.
                            Available at: <a href="https://www.tandfonline.com/doi/full/10.1080/17425255.2016.1162744" target="_blank" rel="noopener noreferrer">https://www.tandfonline.com/doi/full/10.1080/17425255.2016.1162744</a>
                        </p>
                    </div>
                </div>
            )}
        </div> // End biotinCalculator container
    );
}

export default VitaminBiotinCalculator;